import { Loader2 } from "lucide-react";
import { useConversationContext } from "~/pages/manifold-nextgen/components/conversations/ConversationContext";
import { useLineageDiagram } from "./LineageDiagramContext";
import { MermaidDiagram } from "./MermaidDiagram";
import { useArtifactLineage } from "./useArtifactLineage";

interface ArtifactLineageProps {
  artifactId: string;
}

// Body of an ArtifactShell when the Lineage tab is selected — exact parallel to
// ArtifactCode. Draws the diagram the surrounding card supplies, if any, and
// otherwise the one returned by the lineage endpoint.
export const ArtifactLineage = ({ artifactId }: ArtifactLineageProps) => {
  const diagram = useLineageDiagram();
  if (diagram !== null) {
    return <MermaidDiagram source={diagram} idPrefix={`lineage-${artifactId}`} />;
  }
  return <FetchedArtifactLineage artifactId={artifactId} />;
};

const FetchedArtifactLineage = ({ artifactId }: ArtifactLineageProps) => {
  const scope = useConversationContext();
  const { lineage, error, isLoading } = useArtifactLineage(scope, artifactId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2
          className="size-5 animate-spin text-muted-foreground"
          aria-label="Loading lineage"
        />
      </div>
    );
  }
  if (error) {
    return <p className="py-3 text-xs text-destructive">Failed to load lineage: {String(error)}</p>;
  }
  if (!lineage || !lineage.nodes?.length) {
    return (
      <p className="py-3 text-xs text-muted-foreground">No lineage found for this artifact.</p>
    );
  }
  return (
    <>
      <p className="mb-3 text-xs text-muted-foreground">
        This diagram shows how this artifact was created, including all parent artifacts and source
        tables that contributed to it.
      </p>
      <MermaidDiagram source={lineage.mermaid} idPrefix={`lineage-${artifactId}`} />
    </>
  );
};
